'use client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { PRODUCT_MUTATIONS, PRODUCT_QUERIES } from '../../infrastructure/product/utils/queries';

import type {
  ProductsAllRequest,
  ProductsCollectionRequest,
  DiscountCampaignProductsRequest,
  ProductsByIdsRequest,
  ProductsBySkusRequest,
  ProductsBySkusResponse,
  TopRankedProductsRequest,
  ProductsCollectionCompetitorRequest,
  ProductBrowseRequest,
  ProductsDetailRequest,
  ProductsDetailResponse,
  ProductMetaRequest,
  ProductMetaResponse,
  ProductIngredientsRequest,
  ProductIngredientsResponse,
  ProductSearchRequest,
  AIInsightRequest,
  QueryListRecommendationsRequest,
  ProductsRecommendRequest,
  RoutineRecommendationsRequest,
} from '../../infrastructure/product/utils/types';

export const useProductService = () => {
  const useGetProductsAll = (params: ProductsAllRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductsAll(params));
  };

  const useGetProductsCollections = (params: ProductsCollectionRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductsCollections(params));
  };
  const useGetProductsCollectionsQuery = (params: ProductsCollectionRequest) => {
    return useQuery(PRODUCT_QUERIES.getProductsCollections(params));
  };

  const useGetDiscountCampaignProductsInfinite = (params: DiscountCampaignProductsRequest) => {
    return useInfiniteQuery(PRODUCT_QUERIES.getDiscountCampaignProductsInfinite(params, useQueryClient()));
  };

  const useGetSuspenseProductsByIds = (params: ProductsByIdsRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductsByIds(params));
  };
  const useGetProductsByIds = (params: ProductsByIdsRequest) => {
    return useQuery(PRODUCT_QUERIES.getProductsByIds(params));
  };

  const useGetProductsByIdsQuery = (params: ProductsByIdsRequest) => {
    return useQuery(PRODUCT_QUERIES.getProductsByIds(params));
  };

  const useSuspenseProductsBySkus = (params: ProductsBySkusRequest) => {
    return useSuspenseQuery<ProductsBySkusResponse>(PRODUCT_QUERIES.getProductsBySkus(params));
  };

  const useGetProductsDetail = (params: ProductsDetailRequest) => {
    return useSuspenseQuery<ProductsDetailResponse>(PRODUCT_QUERIES.getProductsDetail(params));
  };

  const useGetProductsDetailWithQuery = (params: ProductsDetailRequest) => {
    return useQuery<ProductsDetailResponse>(PRODUCT_QUERIES.getProductsDetail(params));
  };

  const useGetProductMeta = (params: ProductMetaRequest) => {
    return useSuspenseQuery<ProductMetaResponse>(PRODUCT_QUERIES.getProductMeta(params));
  };

  const useSuspenseProductIngredients = (params: ProductIngredientsRequest) => {
    return useSuspenseQuery<ProductIngredientsResponse>(PRODUCT_QUERIES.getProductIngredients(params));
  };

  const useGetProductsByIdsMutation = () => {
    return useMutation(PRODUCT_MUTATIONS.getProductsByIds());
  };

  const useGetTopRankedProducts = (params: TopRankedProductsRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getTopRankedProducts(params));
  };

  const useGetTopRankedProductsQuery = (params: TopRankedProductsRequest) => {
    return useQuery(PRODUCT_QUERIES.getTopRankedProducts(params));
  };

  const useGetProductTaxonomy = (options?: { enabled?: boolean }) => {
    return useQuery({
      ...PRODUCT_QUERIES.getProductTaxonomy(),
      ...options,
    });
  };

  const useGetSuspenseProductTaxonomy = () => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductTaxonomy());
  };

  const useGetProductBrandTaxonomy = () => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductBrandTaxonomy());
  };

  const useGetProductsCollectionCompetitor = (params: ProductsCollectionCompetitorRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductsCollectionCompetitor(params));
  };

  const useGetCategoryTaxonomy = () => {
    return useSuspenseQuery(PRODUCT_QUERIES.getCategoryTaxonomy());
  };

  const useGetSuspenseAllCategoryTaxonomy = () => {
    return useSuspenseQuery(PRODUCT_QUERIES.getAllCategoryTaxonomy());
  };

  const useGetAllCategoryTaxonomyQuery = () => {
    return useQuery(PRODUCT_QUERIES.getAllCategoryTaxonomy());
  };

  const useGetProductBrowse = (params?: ProductBrowseRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductBrowse(params));
  };

  const useGetProductSearch = (params: ProductSearchRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductSearch(params));
  };

  const useGetSuspenseListRecommendations = (params: QueryListRecommendationsRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getListRecommendations(params));
  };
  const useGetListRecommendations = (params: QueryListRecommendationsRequest) => {
    return useQuery(PRODUCT_QUERIES.getListRecommendations(params));
  };

  const useGetAIInsightMutation = () => {
    return useMutation(PRODUCT_MUTATIONS.getAIInsight());
  };

  const useGetSuspenseAIInsight = (params: AIInsightRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getAIInsight(params));
  };

  const useGetAIInsight = (params: AIInsightRequest) => {
    return useQuery(PRODUCT_QUERIES.getAIInsight(params));
  };

  const useGetRecommendedProductsSuspenseQuery = (params: ProductsRecommendRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getRecommendedProducts(params));
  };

  const useGetRecommendedProductsQuery = (params: ProductsRecommendRequest, options?: { enabled?: boolean }) => {
    return useQuery({
      ...PRODUCT_QUERIES.getRecommendedProducts(params),
      ...options,
    });
  };

  const useGetRoutineRecommendationsSuspenseQuery = (params: RoutineRecommendationsRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getRoutineRecommendations(params));
  };

  const useGetRoutineRecommendationsQuery = (
    params: RoutineRecommendationsRequest,
    options?: { enabled?: boolean },
  ) => {
    return useQuery({
      ...PRODUCT_QUERIES.getRoutineRecommendations(params),
      ...options,
    });
  };

  return {
    useGetProductsAll,
    useGetProductsCollections,
    useGetProductsCollectionsQuery,
    useGetDiscountCampaignProductsInfinite,
    useGetSuspenseProductsByIds,
    useGetProductsByIds,
    useSuspenseProductsBySkus,
    useGetProductsDetail,
    useGetProductMeta,
    useSuspenseProductIngredients,
    useGetProductsByIdsMutation,
    useGetTopRankedProducts,
    useGetTopRankedProductsQuery,
    useGetSuspenseProductTaxonomy,
    useGetProductTaxonomy,
    useGetProductBrandTaxonomy,
    useGetProductsCollectionCompetitor,
    useGetCategoryTaxonomy,
    useGetSuspenseAllCategoryTaxonomy,
    useGetAllCategoryTaxonomyQuery,
    useGetProductBrowse,
    useGetProductSearch,
    useGetSuspenseListRecommendations,
    useGetListRecommendations,
    useGetAIInsightMutation,
    useGetSuspenseAIInsight,
    useGetAIInsight,
    useGetProductsDetailWithQuery,
    useGetProductsByIdsQuery,
    useGetRecommendedProductsSuspenseQuery,
    useGetRecommendedProductsQuery,
    useGetRoutineRecommendationsSuspenseQuery,
    useGetRoutineRecommendationsQuery,
  };
};
